import { useAvailableShopsQuery } from '@@partner:graphql/query/catalog/availableShops.gql'
import { price, ProductPrice, Shop } from '@@partner:types:app'
import { GQL_ID } from '@@partner:types:graphql'
import AppLoader from '@components/common/AppLoader'
import React, { useEffect, useState } from 'react'
import { Col, Row, Table, Tag } from 'rsuite'
import ProductShopsSelector from '../ProductShopSelector'

interface ProductPriceState {
  prices: ProductPrice[]
}

type props = {
  shopIds: GQL_ID[]
  prices: ProductPrice[]
  loadingProduct: boolean
  onShopSelect?: (selectedShopIds: GQL_ID[]) => void
}

const { Column, HeaderCell, Cell } = Table

export default function ProductPricesTable ({
  shopIds,
  prices,
  loadingProduct,
  onShopSelect
}: props) {
  const { data, loading } = useAvailableShopsQuery(
    {
      ids: shopIds
    },
    shopIds.length === 0
  )

  const [ state, setState ] = useState<ProductPriceState>({
    prices
  })

  useEffect(() => {
    setState({ prices })
  }, [ prices ])

  // Function to format price with currency symbol
  const formatPrice = (value: price) => {
    return `${value} ${import.meta.env.VITE_APP_CURRENCY_SYMBOL}`
  }

  // Function to check if a sale price is active
  const isSaleActive = (price: ProductPrice) => {
    if (!price.discount_price) return false
    
    const now = new Date().getTime()
    const start = price.salePeriod?.start ? Number(price.salePeriod.start): 0
    const finish = price.salePeriod?.finish ? Number(price.salePeriod.finish) : Infinity
    
    return now >= start && now <= finish
  }

  return (
    <Row>
      <Row>
        <Col xs={24} md={8}>
          <ProductShopsSelector value={shopIds} onChange={onShopSelect} />
        </Col>
      </Row>
      
      <Row style={{ marginTop: '20px', width: '100%' }}>
        <Table
          data={state.prices}
          rowHeight={60}
          autoHeight
          loading={loading || loadingProduct}
          renderLoading={() =>
            loading ? <AppLoader.Inner content="Загрузка таблицы" /> : null
          }
          affixHeader
        >
          <Column verticalAlign="middle">
            <HeaderCell>Магазин</HeaderCell>
            <Cell dataKey="shop_id" />
          </Column>
          <Column flexGrow={1} verticalAlign="middle">
            <HeaderCell>Название магазина</HeaderCell>
            <Cell dataKey="shop_id">
              {(priceData) => (
                <div>{ data?.shops.find(shop => shop.id === priceData.shop_id)?.name }</div>
              )}
            </Cell>
          </Column>
          <Column flexGrow={1} verticalAlign="middle">
            <HeaderCell>Адрес</HeaderCell>
            <Cell dataKey="shop_id">
              {(priceData) => (
                <div>{ data?.shops.find(shop => shop.id === priceData.shop_id)?.address }</div>
              )}
            </Cell>
          </Column>
          <Column width={150} verticalAlign="middle">
            <HeaderCell>Обычная цена</HeaderCell>
            <Cell dataKey="price">
              {(priceData) => (
                <div>{formatPrice(priceData.price)}</div>
              )}
            </Cell>
          </Column>
          <Column width={150} verticalAlign="middle">
            <HeaderCell>Цена со скидкой</HeaderCell>
            <Cell dataKey="discount_price">
              {(priceData: ProductPrice) => (
                <div>
                  {priceData.discount_price ? (
                    <>
                      <div>{formatPrice(priceData.discount_price)}</div>
                      <Tag color={isSaleActive(priceData) ? 'green' : 'red'} size="sm">
                        {isSaleActive(priceData) ? 'Активна' : 'Неактивна'}
                      </Tag>
                    </>
                  ) : (
                    '-'
                  )}
                </div>
              )}
            </Cell>
          </Column>
          <Column width={150} verticalAlign="middle">
            <HeaderCell>Период скидки</HeaderCell>
            <Cell>
              {(priceData: ProductPrice) => (
                <div>
                  {priceData.salePeriod?.start && priceData.salePeriod?.finish ? (
                    <>
                      <div>{new Date(Number(priceData.salePeriod.start)).toLocaleDateString()}</div>
                      <div>{new Date(Number(priceData.salePeriod.finish)).toLocaleDateString()}</div>
                    </>
                  ) : (
                    '-'
                  )}
                </div>
              )}
            </Cell>
          </Column>
          <Column width={120} verticalAlign="middle">
            <HeaderCell>Множитель</HeaderCell>
            <Cell dataKey="unit_multiplier">
              {(priceData) => (
                <div>{priceData.unit_multiplier || 1}</div>
              )}
            </Cell>
          </Column>
          <Column width={150} verticalAlign="middle">
            <HeaderCell>Параметры UI</HeaderCell>
            <Cell>
              {(priceData: ProductPrice) => (
                <div>
                  <div>Минимум: { priceData.min }</div>
                  <div>Шаг: { priceData.step }</div>
                  <div>Максимум: { priceData.max }</div>
                </div>
              )}
            </Cell>
          </Column>
        </Table>
      </Row>
    </Row>
  )
}
